import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import Box from '@material-ui/core/Box';
import { IconButton } from '@material-ui/core';
import { BaseModal, Table } from 'components/UI';
import DeleteIcon from '@material-ui/icons/Delete';
import { usersActions } from 'state/modules/users';
import Typography from '@material-ui/core/Typography';
import { UserGroupsSelect } from 'components/AsyncFields';
import { useStyles } from './AssignUserGroupsModal.styled';

const AssignUserGroupsModal = ({ id, open, handleClick, selectCustomParams }) => {
  const classes = useStyles();
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
  });

  const AssignUserGroupsSchema = Yup.object().shape({
    user_group_id: Yup.string().required(i18n.t('common.required_field')),
  });

  const {
    userGroups,
    fetchingUserGroups,
    fetchingAddUserGroup,
    paginationUserGroups,
    fetchingDeleteUserGroup,
    fetchedAddUserGroupSuccess,
    fetchedDeleteUserGroupSuccess,
  } = useSelector((state) => state.users);

  useEffect(() => {
    if (id) {
      dispatch(usersActions.fetchUserGroups(id, params));
    }
  }, [id]) // eslint-disable-line

  useEffect(() => {
    if (fetchedAddUserGroupSuccess) {
      if (formRef && formRef.current) {
        formRef.current.resetForm();
      }
      dispatch(usersActions.fetchUserGroups(id));
    }
  }, [id, fetchedAddUserGroupSuccess]) // eslint-disable-line

  useEffect(() => {
    if (fetchedDeleteUserGroupSuccess) {
      dispatch(usersActions.fetchUserGroups(id));
    }
  }, [id, fetchedDeleteUserGroupSuccess]) // eslint-disable-line

  const userGroupsPayload = {
    limit: 20,
    ...selectCustomParams,
    // show all user groups
    // ticket: MC3-1534
  };

  const onSubmit = (values) => {
    dispatch(usersActions.fetchAddUserGroups(id, values.user_group_id));
  };

  const columns = [
    {
      name: i18n.t('user_groups.id'),
      selector: 'id',
      grow: 1,
    },
    {
      name: i18n.t('user_groups.name'),
      selector: 'name',
      grow: 4,
    },
    {
      name: i18n.t('user_groups.actions'),
      selector: '',
      grow: 1,
      cell: (row) => (
        <IconButton onClick={() => handleDeleteItem(row)}>
          {' '}
          <DeleteIcon />{' '}
        </IconButton>
      ),
    },
  ];

  const handleDeleteItem = (item) => {
    dispatch(usersActions.fetchDeleteUserGroups(id, item.id));
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  const handleCancel = () => {
    dispatch(usersActions.resetAddVehicleGroups());
    handleClick();
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    setParams({ ...params, ...newParams });
    dispatch(usersActions.fetchUserGroups(id, { ...params, ...newParams }));
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    setParams({ ...params, ...newParams });
    dispatch(usersActions.fetchUserGroups(id, { ...params, ...newParams }));
  };

  return (
    <BaseModal
      size="md"
      open={open}
      maxWidth={550}
      hasEnterButton
      hasCancelButton
      onEnter={handleSubmit}
      onCancel={handleCancel}
      enableCloseButton={false}
      title={i18n.t('user_groups.add_user_group')}
      enterButtonLabel={i18n.t('user_groups.add_user_group')}
      loading={fetchingAddUserGroup || fetchingDeleteUserGroup}
    >
      <>
        <Typography variant="body2" gutterBottom>
          <Box mb={3} fontSize={13} component="span">
            {i18n.t('user_groups.assigned_groups')}:
          </Box>
        </Typography>
        <Box mt={2} width="100%" className={classes.tableBody}>
          <Table
            data={userGroups}
            columns={columns}
            loading={fetchingUserGroups}
            pagination={true}
            totalRows={paginationUserGroups?.totalRows || 0}
            progressPending={fetchingUserGroups}
            handlePageChange={handlePageChange}
            handlePerRowsChange={handlePerRowsChange}
          />
          <Formik
            innerRef={formRef}
            onSubmit={onSubmit}
            initialValues={{ user_group_id: '' }}
            validationSchema={AssignUserGroupsSchema}
          >
            {({ handleSubmit }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Box mt={1} maxWidth={'80%'}>
                  <Field
                    name="user_group_id"
                    label="User Groups"
                    omitFields={[...userGroups.map((i) => i.name)]}
                    omitFieldKey={'name'}
                    customParams={userGroupsPayload}
                    component={UserGroupsSelect}
                  />
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </>
    </BaseModal>
  );
};

AssignUserGroupsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

AssignUserGroupsModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default AssignUserGroupsModal;
